/**
 * Economic indicators panel — FRED data display.
 * GDP growth, unemployment, CPI, fed funds rate.
 * Central bank rate comparison table.
 * Sourced from FRED events in the store.
 */

import { appStore } from '@/core/store';
import { eventBus } from '@/core/events';
import type { NormalizedEvent } from '@/sources/base-source';
import { createTrendSparkline } from '@/ui/components/sparkline';

/** Known FRED series metadata for display. */
const SERIES_META: Record<string, { label: string; unit: string; icon: string }> = {
  GDP: { label: 'GDP (Quarterly)', unit: 'B$', icon: '\uD83D\uDCC8' },
  UNRATE: { label: 'Unemployment', unit: '%', icon: '\uD83D\uDC64' },
  CPIAUCSL: { label: 'CPI Inflation', unit: 'Index', icon: '\uD83D\uDCB0' },
  FEDFUNDS: { label: 'Fed Funds Rate', unit: '%', icon: '\uD83C\uDFE6' },
  T10Y2Y: { label: '10Y-2Y Spread', unit: '%', icon: '\uD83D\uDCC9' },
  DEXUSEU: { label: 'USD/EUR', unit: '', icon: '\uD83D\uDCB1' },
};

/** Central bank rate data (manually maintained reference). */
const CENTRAL_BANK_RATES: { name: string; country: string; rate: string; lastUpdated: string }[] = [
  { name: 'Federal Reserve', country: 'US', rate: '5.25-5.50', lastUpdated: '2024-09' },
  { name: 'ECB', country: 'EU', rate: '4.50', lastUpdated: '2024-09' },
  { name: 'Bank of England', country: 'UK', rate: '5.25', lastUpdated: '2024-09' },
  { name: 'Bank of Japan', country: 'JP', rate: '0.25', lastUpdated: '2024-07' },
  { name: 'PBoC (China)', country: 'CN', rate: '3.35', lastUpdated: '2024-07' },
  { name: 'RBI (India)', country: 'IN', rate: '6.50', lastUpdated: '2024-06' },
  { name: 'Bank of Canada', country: 'CA', rate: '4.50', lastUpdated: '2024-09' },
  { name: 'RBA (Australia)', country: 'AU', rate: '4.35', lastUpdated: '2023-11' },
];

/** Track FRED values over time for sparklines (session-local). */
const seriesHistory = new Map<string, number[]>();
const MAX_HISTORY = 20;

export function createEconomicPanel(): HTMLElement {
  const container = document.createElement('div');
  container.className = 'economic-panel';
  container.style.cssText = 'display: flex; flex-direction: column; height: 100%; overflow-y: auto;';

  const contentArea = document.createElement('div');
  contentArea.style.cssText = 'padding: 12px;';
  container.appendChild(contentArea);

  const cleanupFns: (() => void)[] = [];
  let renderPending = false;

  function scheduleRender(): void {
    if (renderPending) return;
    renderPending = true;
    requestAnimationFrame(() => {
      renderPending = false;
      render();
    });
  }

  function render(): void {
    const events = appStore.getState().events;
    contentArea.textContent = '';

    // Extract FRED events from store
    const fredEvents = events.filter(e => e.source === 'fred');

    // FRED Indicators section
    const indicatorHeader = document.createElement('div');
    indicatorHeader.style.cssText = 'font-size: 11px; font-weight: 600; color: var(--text-secondary); margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.05em;';
    indicatorHeader.textContent = 'Economic Indicators (FRED)';
    contentArea.appendChild(indicatorHeader);

    if (fredEvents.length === 0) {
      const msg = document.createElement('div');
      msg.style.cssText = 'font-size: 11px; color: var(--text-muted); font-style: italic; padding: 16px 0; text-align: center;';
      msg.textContent = 'Waiting for FRED data\u2026 (requires API key)';
      contentArea.appendChild(msg);
    } else {
      const grid = document.createElement('div');
      grid.style.cssText = 'display: flex; flex-direction: column; gap: 4px; margin-bottom: 16px;';

      for (const ev of fredEvents) {
        grid.appendChild(renderIndicatorCard(ev));
      }

      contentArea.appendChild(grid);
    }

    // Central Bank Rates section
    const rateHeader = document.createElement('div');
    rateHeader.style.cssText = 'font-size: 11px; font-weight: 600; color: var(--text-secondary); margin-bottom: 8px; margin-top: 12px; text-transform: uppercase; letter-spacing: 0.05em;';
    rateHeader.textContent = 'Central Bank Policy Rates';
    contentArea.appendChild(rateHeader);

    const rateNote = document.createElement('div');
    rateNote.style.cssText = 'font-size: 9px; color: var(--text-muted); margin-bottom: 8px;';
    rateNote.textContent = 'Reference rates — may not reflect latest changes';
    contentArea.appendChild(rateNote);

    const table = document.createElement('div');
    table.style.cssText = 'display: flex; flex-direction: column; gap: 2px;';

    // Table header
    const tableHeader = document.createElement('div');
    tableHeader.style.cssText = `
      display: flex; gap: 4px; padding: 4px 8px;
      font-size: 10px; font-weight: 600; color: var(--text-muted);
      border-bottom: 1px solid var(--border-primary);
    `;
    for (const [text, flex] of [['Bank', '1'], ['Rate', '0 0 60px'], ['Updated', '0 0 70px']] as const) {
      const col = document.createElement('span');
      col.style.cssText = `flex: ${flex};`;
      col.textContent = text;
      tableHeader.appendChild(col);
    }
    table.appendChild(tableHeader);

    for (const bank of CENTRAL_BANK_RATES) {
      const row = document.createElement('div');
      row.style.cssText = `
        display: flex; gap: 4px; padding: 4px 8px; border-radius: 3px;
        background: var(--bg-secondary); font-size: 11px;
      `;

      const nameCol = document.createElement('span');
      nameCol.style.cssText = 'flex: 1; color: var(--text-primary); white-space: nowrap; overflow: hidden; text-overflow: ellipsis;';
      nameCol.textContent = `${bank.name} (${bank.country})`;
      row.appendChild(nameCol);

      const rateCol = document.createElement('span');
      rateCol.style.cssText = 'flex: 0 0 60px; font-family: var(--font-mono); font-weight: 600; color: var(--accent-blue); text-align: right;';
      rateCol.textContent = `${bank.rate}%`;
      row.appendChild(rateCol);

      const dateCol = document.createElement('span');
      dateCol.style.cssText = 'flex: 0 0 70px; color: var(--text-muted); font-size: 10px; text-align: right;';
      dateCol.textContent = bank.lastUpdated;
      row.appendChild(dateCol);

      table.appendChild(row);
    }

    contentArea.appendChild(table);
  }

  function renderIndicatorCard(ev: NormalizedEvent): HTMLElement {
    const raw = ev.raw as { seriesId?: string; value?: number; change?: number; unit?: string } | undefined;
    const seriesId = raw?.seriesId ?? '';
    const meta = SERIES_META[seriesId];
    const value = raw?.value;
    const change = raw?.change;

    // Record history for sparkline
    if (seriesId && value !== undefined) {
      let history = seriesHistory.get(seriesId);
      if (!history) {
        history = [];
        seriesHistory.set(seriesId, history);
      }
      // Only push if value differs from last recorded
      if (history.length === 0 || history[history.length - 1] !== value) {
        history.push(value);
        if (history.length > MAX_HISTORY) history.shift();
      }
    }

    const card = document.createElement('div');
    card.style.cssText = `
      display: flex; align-items: center; gap: 8px;
      padding: 8px 10px; border-radius: 6px;
      background: var(--bg-secondary); border: 1px solid var(--border-primary);
    `;

    // Label
    const labelCol = document.createElement('div');
    labelCol.style.cssText = 'flex: 1; min-width: 0;';

    const labelEl = document.createElement('div');
    labelEl.style.cssText = 'font-size: 11px; font-weight: 500; color: var(--text-primary);';
    labelEl.textContent = meta?.label ?? seriesId;
    labelCol.appendChild(labelEl);

    if (change !== undefined) {
      const changeEl = document.createElement('div');
      const isPos = change >= 0;
      changeEl.style.cssText = `font-size: 10px; font-family: var(--font-mono); color: ${isPos ? 'var(--accent-green)' : 'var(--accent-red)'};`;
      changeEl.textContent = `${isPos ? '+' : ''}${change.toFixed(2)}`;
      labelCol.appendChild(changeEl);
    }

    card.appendChild(labelCol);

    // Sparkline
    const history = seriesHistory.get(seriesId);
    if (history && history.length >= 2) {
      const sparkEl = createTrendSparkline(history, { width: 60, height: 18 });
      card.appendChild(sparkEl);
    }

    // Value
    const valueEl = document.createElement('div');
    valueEl.style.cssText = 'font-size: 14px; font-weight: 700; font-family: var(--font-mono); color: var(--text-primary); flex-shrink: 0;';
    valueEl.textContent = value !== undefined ? value.toFixed(2) : '—';
    card.appendChild(valueEl);

    // Unit
    if (meta?.unit) {
      const unitEl = document.createElement('div');
      unitEl.style.cssText = 'font-size: 9px; color: var(--text-muted); flex-shrink: 0; width: 24px;';
      unitEl.textContent = meta.unit;
      card.appendChild(unitEl);
    }

    return card;
  }

  // Initial render
  render();

  // Subscribe to event changes
  cleanupFns.push(
    appStore.subscribe(
      (s) => s.events,
      () => scheduleRender(),
    ),
  );

  // Refresh on analysis complete
  cleanupFns.push(
    eventBus.on('analysis:complete', () => scheduleRender()),
  );

  (container as HTMLElement & { destroy?: () => void }).destroy = () => {
    for (const fn of cleanupFns) fn();
    cleanupFns.length = 0;
  };

  return container;
}
